import type { FormerNameType, NameProvenance } from "src/graphql/generated";

/**
 * The kinds of former name, in the order the form offers them.
 *
 * The two that assert a name the person legally held come first: they are the common
 * case, and each carries the hint that tells a writer which one to pick, since maiden
 * is a kind of prior legal name and romanization is a kind of transliteration. The
 * rule is to choose the specific one wherever it applies.
 *
 * The hints render everywhere the kind is chosen: the identity screen's name-change
 * panel, where it must be answered before a legal-name edit will save, and the create
 * form's former-name rows, where Kind sits on its own line beneath the grid so there
 * is room for them. A writer picking from five bare labels cannot tell which applies,
 * which is the whole reason the hints exist.
 */
export const FORMER_NAME_TYPES: { value: FormerNameType; label: string; hint: string }[] = [
  { value: "MAIDEN", label: "Maiden name", hint: "The pre-marriage name" },
  {
    value: "PRIOR_LEGAL",
    label: "Prior legal name",
    hint: "Any other name they legally held — deed poll, adoption, transition"
  },
  {
    value: "ROMANIZATION",
    label: "Romanization",
    hint: "A Latin-script rendering of the same name, such as on an older visa"
  },
  { value: "TRANSLITERATION", label: "Transliteration", hint: "A rendering into another script" },
  {
    value: "CORRECTION",
    label: "Correction",
    hint: "A mistaken value in our records, such as a typo"
  }
];

export function humanFormerNameType(value: FormerNameType): string {
  return FORMER_NAME_TYPES.find((t) => t.value === value)?.label ?? value;
}

/** Whether anyone checked the name against a document. */
export const NAME_PROVENANCES: { value: NameProvenance; label: string }[] = [
  { value: "SELF_REPORTED", label: "Self-reported" },
  { value: "DOCUMENT_VERIFIED", label: "Document-verified" }
];

export function humanNameProvenance(value: NameProvenance): string {
  return NAME_PROVENANCES.find((p) => p.value === value)?.label ?? value;
}

export type FormerNameSummary = {
  nameType?: FormerNameType | null;
  validFrom?: string | null;
  validTo?: string | null;
  provenance?: NameProvenance | null;
};

/**
 * One line describing what is recorded about a former name, beside the name itself.
 *
 * "Kind not recorded" is said rather than left blank. Blank would read as a plain
 * prior name, and the rows that predate the field are exactly the ones nobody has
 * confirmed — an unverified claim about a legal name is worse in a background check
 * than a missing one, so the gap has to show.
 */
export function describeFormerName(name: FormerNameSummary): string {
  const parts: string[] = [
    name.nameType ? humanFormerNameType(name.nameType) : "Kind not recorded"
  ];
  if (name.validFrom && name.validTo) {
    parts.push(`${name.validFrom} to ${name.validTo}`);
  } else if (name.validTo) {
    parts.push(`until ${name.validTo}`);
  } else if (name.validFrom) {
    parts.push(`from ${name.validFrom}`);
  }
  if (name.provenance) {
    parts.push(humanNameProvenance(name.provenance));
  }
  return parts.join(" · ");
}

/**
 * The legal names a record holds, in the two renderings it holds them in.
 *
 * Preferred names are absent, and that is the rule rather than an omission: they
 * are not legal identity, and asking for a former-name record to change a nickname
 * would be wrong as well as tiresome.
 */
export type LegalNames = {
  legalFirstName?: string | null;
  legalMiddleName?: string | null;
  legalLastName?: string | null;
  legalFullNameLocal?: string | null;
};

/**
 * One name part, in the one form two spellings of it can be compared in.
 *
 * Precomposed and decomposed Hangul render identically and differ character for
 * character, so comparing raw would read a re-encoded paste as a name change — and
 * ask the operator to explain one. The API canonicalises the same way, on the way in
 * and on comparison.
 */
function part(value: string | null | undefined): string {
  return (value ?? "").trim().normalize("NFC");
}

/** The composed legal name, the same join the API derives `legalFullName` with. */
export function composeLegalName(names: LegalNames): string {
  return [names.legalFirstName, names.legalMiddleName, names.legalLastName]
    .map(part)
    .filter(Boolean)
    .join(" ");
}

/**
 * The legal names an edit would replace — the values as the record holds them.
 *
 * Two renderings, because the record holds two: the composed name and the
 * local-script one. Either changes without the other, so each is a former name of
 * its own when its own value is the one being replaced.
 *
 * What is recorded is the whole composed name rather than the part that moved:
 * "Roe" is not a name, and one edit that changes three columns replaced one name.
 * But only the parts the record already held count towards *whether* a name was
 * replaced — filling in a middle name off a passport completes the record, and the
 * shorter composition it leaves behind was never the person's legal name.
 *
 * This mirrors `outgoingLegalNames` in the store, and the store is authoritative —
 * it refuses a legal-name change that arrives without its reason and refuses a
 * reason with nothing to explain. What this decides is only whether to *ask*, so a
 * drift between the two shows up as a rejected save rather than a bad record.
 */
export function outgoingLegalNames(before: LegalNames, after: LegalNames): string[] {
  const out: string[] = [];
  if (replacedARecordedPart(before, after)) {
    out.push(composeLegalName(before));
  }
  const localBefore = part(before.legalFullNameLocal);
  if (localBefore && localBefore !== part(after.legalFullNameLocal)) {
    out.push(localBefore);
  }
  return out;
}

/** Whether a part of the composed name the record already held is now different. */
function replacedARecordedPart(before: LegalNames, after: LegalNames): boolean {
  const parts = ["legalFirstName", "legalMiddleName", "legalLastName"] as const;
  return parts.some((key) => {
    const was = part(before[key]);
    return was !== "" && was !== part(after[key]);
  });
}

/** One row of the create form's former-name editor. */
export type FormerNameRow = {
  fullName: string;
  nameType: FormerNameType | "";
  validFrom: string;
  validTo: string;
  provenance: NameProvenance | "";
};

export function emptyFormerNameRow(): FormerNameRow {
  return { fullName: "", nameType: "", validFrom: "", validTo: "", provenance: "" };
}

function isBlank(row: FormerNameRow): boolean {
  return !row.fullName.trim() && !row.nameType && !row.validFrom && !row.validTo && !row.provenance;
}

/**
 * The rows that name nothing while recording something about it.
 *
 * The backend refuses these rather than dropping them, because dropping one would
 * discard what somebody typed and still report the create as a success. The form
 * blocks submission on the same rule so the refusal does not arrive as a toast after
 * the fact.
 */
export function rowsMissingAName(rows: FormerNameRow[]): boolean {
  return rows.some((row) => !row.fullName.trim() && !isBlank(row));
}

/**
 * The rows to send, in order, with the untouched ones dropped.
 *
 * A row is untouched when nothing at all is filled in — adding a row and leaving it
 * unused is not a mistake. Empty optional fields are sent as
 * undefined rather than "": the API's Date scalar refuses an empty string, and the
 * enums have no empty member.
 */
export function formerNamesInput(rows: FormerNameRow[]) {
  return rows
    .filter((row) => !isBlank(row))
    .map((row) => ({
      fullName: row.fullName.trim(),
      nameType: row.nameType || undefined,
      validFrom: row.validFrom || undefined,
      validTo: row.validTo || undefined,
      provenance: row.provenance || undefined
    }));
}
